import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Calculator, Printer, Copy, ReceiptText } from 'lucide-react';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'card', label: 'Card' },
  { value: 'online_payment', label: 'Jjuma Pay online' },
];

function money(currency, value) {
  return `${currency} ${Number(value || 0).toLocaleString()}`;
}

function StatusBadge({ status }) {
  const styles = {
    paid: 'bg-emerald-100 text-emerald-800',
    pending: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-slate-100 text-slate-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
      {status || '-'}
    </span>
  );
}

export default function PosPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ sales: [], daily_sales: { today: {}, week: {}, month: {} } });
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [saleDetail, setSaleDetail] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    item_mode: 'product',
    item_name: '',
    unit_price: '',
    quantity: 1,
    currency: 'UGX',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    discount: '',
    tax: '',
    payment_method: 'cash',
    notes: '',
  });

  const loadSales = async () => {
    const res = await api.get('/dashboard/pos-sales');
    setData(res.data || {});
    return res.data;
  };

  const loadStores = async () => {
    const res = await api.get('/dashboard/stores');
    const nextStores = res.data?.stores || [];
    setStores(nextStores);
    const first = nextStores[0];
    if (first) {
      setSelectedStoreId((prev) => prev || first.id);
      setForm((prev) => ({ ...prev, currency: first.currency || prev.currency }));
    }
    return res.data;
  };

  const loadProducts = async (storeId) => {
    if (!storeId) {
      setProducts([]);
      return;
    }
    const res = await api.get(`/dashboard/stores/${storeId}/products`);
    setProducts(res.data?.products || []);
    const first = res.data?.products?.[0];
    if (first && form.item_mode === 'product') {
      setSelectedProductId(first.id);
      setForm((prev) => ({
        ...prev,
        item_name: first.name || '',
        unit_price: first.sale_price || first.price || '',
        currency: first.currency || prev.currency,
      }));
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadStores(), loadSales()])
      .catch(() => toast.error('Could not load POS data'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedStoreId) return;
    loadProducts(selectedStoreId).catch(() => setProducts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStoreId]);

  const selectedStore = stores.find((s) => s.id === selectedStoreId) || stores[0] || null;

  const sales = data.sales || [];
  const daily = data.daily_sales || {};
  const subtotal = Number(form.quantity || 1) * Number(form.unit_price || 0);
  const total = Math.max(0, subtotal - Number(form.discount || 0) + Number(form.tax || 0));

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProductChange = (productId) => {
    setSelectedProductId(productId);
    const product = products.find((item) => item.id === productId);
    if (!product) return;
    setForm((prev) => ({
      ...prev,
      item_mode: 'product',
      item_name: product.name || '',
      unit_price: product.sale_price || product.price || '',
      currency: product.currency || prev.currency,
    }));
  };

  const handleCopyReceipt = async () => {
    if (!saleDetail?.receipt_text) return;
    try {
      await navigator.clipboard.writeText(saleDetail.receipt_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('Could not copy receipt');
    }
  };

  const openSale = async (saleId) => {
    try {
      const res = await api.get(`/dashboard/pos-sales/${saleId}`);
      setSaleDetail(res.data?.sale || null);
    } catch {
      toast.error('Could not load sale');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      toast.error('Please create a store first.');
      return;
    }
    if (!form.item_name.trim()) {
      toast.error('Item name is required.');
      return;
    }
    if (Number(form.unit_price || 0) <= 0) {
      toast.error('Enter a valid item price.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        store_id: selectedStoreId,
        currency: form.currency,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email,
        discount: form.discount,
        tax: form.tax,
        payment_method: form.payment_method,
        notes: form.notes,
        items: [
          {
            product_id: form.item_mode === 'product' ? selectedProductId : '',
            item_name: form.item_name,
            quantity: Number(form.quantity || 1),
            unit_price: Number(form.unit_price || 0),
            line_total: subtotal,
          },
        ],
      };
      const res = await api.post('/dashboard/pos-sales', payload);
      const sale = res.data?.sale || null;
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      if (sale) {
        setSaleDetail(sale);
      }
      toast.success(res.data?.message || 'Sale saved');
      setForm((prev) => ({
        ...prev,
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        discount: '',
        tax: '',
        notes: '',
        payment_method: 'cash',
      }));
      await loadSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save sale');
    } finally {
      setSaving(false);
    }
  };

  const quickStats = [
    { label: 'Today sales', value: daily.today?.total || 0, count: daily.today?.count || 0 },
    { label: 'This week sales', value: daily.week?.total || 0, count: daily.week?.count || 0 },
    { label: 'This month sales', value: daily.month?.total || 0, count: daily.month?.count || 0 },
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Mini POS</h1>
        <p className="text-slate-500 text-sm">Record quick sales, print receipts and view daily totals.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {quickStats.map((stat) => (
          <Card key={stat.label} className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-5">
              <p className="text-sm font-medium text-slate-500">{stat.label}</p>
              <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(form.currency || 'UGX', stat.value)}</p>
              <p className="text-xs text-slate-400 mt-1">{stat.count} sales</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {!stores.length ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-6">
            <p className="text-slate-500">Create a store first before using POS.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Calculator size={18} /> Record Sale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Store</label>
                    <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                      <SelectTrigger className="mt-1 h-12"><SelectValue placeholder="Select store" /></SelectTrigger>
                      <SelectContent>
                        {stores.map((store) => (
                          <SelectItem key={store.id} value={store.id}>{store.store_name || store.username}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Item type</label>
                    <Select
                      value={form.item_mode}
                      onValueChange={(value) => {
                        update('item_mode', value);
                        if (value === 'custom') {
                          setSelectedProductId('');
                          setForm((prev) => ({ ...prev, item_name: '', unit_price: '' }));
                        } else if (products[0]) {
                          handleProductChange(products[0].id);
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1 h-12"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product">Select product</SelectItem>
                        <SelectItem value="custom">Custom item</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {form.item_mode === 'product' ? (
                  <div>
                    <label className="text-sm font-medium text-slate-700">Product</label>
                    <Select value={selectedProductId} onValueChange={handleProductChange}>
                      <SelectTrigger className="mt-1 h-12"><SelectValue placeholder="Choose product" /></SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Item name</label>
                    <Input value={form.item_name} onChange={(e) => update('item_name', e.target.value)} className="mt-1 h-12" placeholder="Item name" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Quantity</label>
                    <Input type="number" min="1" value={form.quantity} onChange={(e) => update('quantity', e.target.value)} className="mt-1 h-12" />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Unit price</label>
                    <Input type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => update('unit_price', e.target.value)} className="mt-1 h-12" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Currency</label>
                    <Input value={form.currency} onChange={(e) => update('currency', e.target.value.toUpperCase())} className="mt-1 h-12" />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Customer name</label>
                    <Input value={form.customer_name} onChange={(e) => update('customer_name', e.target.value)} className="mt-1 h-12" placeholder="Optional" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Customer phone</label>
                    <Input value={form.customer_phone} onChange={(e) => update('customer_phone', e.target.value)} className="mt-1 h-12" placeholder="Optional" />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Customer email</label>
                    <Input value={form.customer_email} onChange={(e) => update('customer_email', e.target.value)} className="mt-1 h-12" placeholder="Optional" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Payment method</label>
                    <Select value={form.payment_method} onValueChange={(value) => update('payment_method', value)}>
                      <SelectTrigger className="mt-1 h-12"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((method) => (
                          <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Discount</label>
                    <Input type="number" min="0" step="0.01" value={form.discount} onChange={(e) => update('discount', e.target.value)} className="mt-1 h-12" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Tax</label>
                    <Input type="number" min="0" step="0.01" value={form.tax} onChange={(e) => update('tax', e.target.value)} className="mt-1 h-12" />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Notes</label>
                  <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} className="mt-1 min-h-[100px]" placeholder="Optional receipt notes" />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white h-12">
                    {saving ? 'Saving...' : 'Save Sale'}
                  </Button>
                  <div className="text-sm text-slate-500">
                    Estimated total: <span className="font-semibold text-slate-900">{money(form.currency || 'UGX', total)}</span>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <ReceiptText size={18} /> Receipt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {saleDetail ? (
                  <>
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-slate-500">Sale #</p>
                        <p className="text-sm font-semibold text-slate-900">{saleDetail.sale_number}</p>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-slate-500">Status</p>
                        <StatusBadge status={saleDetail.status} />
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-slate-500">Total</p>
                        <p className="text-sm font-semibold text-slate-900">{money(saleDetail.currency, saleDetail.total)}</p>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-slate-500">Receipt</p>
                        <p className="text-sm font-semibold text-slate-900">{saleDetail.receipt_number || '-'}</p>
                      </div>
                    </div>
                    <Textarea readOnly value={saleDetail.receipt_text || ''} className="min-h-[220px] font-mono text-xs" />
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" onClick={handleCopyReceipt}>
                        <Copy size={14} className="mr-1" /> {copied ? 'Copied' : 'Copy receipt'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => window.print()}>
                        <Printer size={14} className="mr-1" /> Print
                      </Button>
                      {saleDetail.receipt_public_url ? (
                        <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.open(saleDetail.receipt_public_url, '_blank', 'noopener,noreferrer')}>
                          Open Receipt Link
                        </Button>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">Saved receipts will appear here.</p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Sales</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sale #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.map((sale) => (
                      <TableRow key={sale.id}>
                        <TableCell className="font-mono text-xs">{sale.sale_number}</TableCell>
                        <TableCell>{sale.customer_name || '-'}</TableCell>
                        <TableCell>{money(sale.currency, sale.total)}</TableCell>
                        <TableCell>{sale.payment_method || '-'}</TableCell>
                        <TableCell><StatusBadge status={sale.status} /></TableCell>
                        <TableCell>
                          <Button type="button" variant="outline" size="sm" onClick={() => openSale(sale.id)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!sales.length && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-8">No sales yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
